/**
 * GameNewsSection — the publisher's own announcements.
 *
 * Built to `devourplay-anchor/Drop-In/team-drops/publisher-drop.html`
 * (`.news` / `.ni`).
 *
 * The feed is Steam's, not ours. A publisher posts patch notes and reveals to
 * their store page whether or not anyone wired it up here; asking them to
 * re-type the same content into our admin is asking them to maintain it twice,
 * which stops happening in week two.
 *
 * Renders nothing at all when there is nothing to show. The anchor has four
 * cards with art on them, and the temptation is to keep the heading and let
 * the grid be empty — but a "Game News" heading over blank space is the exact
 * failure this Drop surface has produced before, so the whole section goes.
 *
 * Each post carries its OWN art, lifted server-side out of the announcement's
 * BBCode body — Steam has no image field, but every announcement opens with an
 * image tag. A post that embeds none keeps the plain tile rather than borrowing
 * the title's key art, which would imply the post is about whatever that art
 * depicts.
 */

import React, {useState} from "react";
/*
 * The generated type, not a local mirror — a hand-written copy of an API shape
 * drifts, and this one did: it was missing `imageUrl` the day it was added.
 */
import {type SteamNewsItem} from "@devour/client";
import "./GameNewsSection.scss";

interface GameNewsSectionProps {
    items: SteamNewsItem[];
}

/*
 * No "All news →" link.
 *
 * It pointed at the title's Steam page — sending a visitor off the publisher's
 * own page to a storefront, from a rail whose entire job is to show the title
 * is actively supported. The "Show more" control below already opens the rest
 * of the feed in place, so the link was an exit with nothing behind it that
 * this page does not already have.
 */

/** Steam reports seconds; JS wants milliseconds. */
function formatDate(seconds: number): string {
    if (!seconds) return "";
    return new Date(seconds * 1000).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

/*
 * Four is a full row at desktop width, and a publisher's page is not a news
 * site — the rail is there to prove the title is being actively supported, not
 * to be read end to end. Everything beyond that is one click away rather than
 * eight cards of scroll between the roster and the rest of the page.
 */
const INITIAL_NEWS_COUNT = 4;

export function GameNewsSection({items}: GameNewsSectionProps): React.ReactElement | null {
    const [showAll, setShowAll] = useState(false);

    if (!items.length) return null;

    const visible = showAll ? items : items.slice(0, INITIAL_NEWS_COUNT);
    const hidden = items.length - visible.length;

    return (
        <section className="game-news" aria-label="Game news">
            <div className="game-news__head">
                <div className="game-news__title-row">
                    <h3 className="game-news__title">Game News</h3>
                </div>
            </div>

            <div className="game-news__grid">
                {visible.map(item =>
                    <a
                        key={item.id}
                        className="ni"
                        href={item.url}
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        {/*
                          * Steam has no image field — the art is embedded in the
                          * post's BBCode and the backend lifts it out. Not every
                          * post has one, so the box keeps its shape and its own
                          * gradient rather than collapsing or showing a broken
                          * image.
                          *
                          * No category tag. The feed is filtered to the
                          * publisher's own announcements, so every card carried
                          * the identical word "ANNOUNCEMENT" over its art —
                          * a label true of the whole rail restated on each item.
                          */}
                        <div className="ni__art">
                            {item.imageUrl &&
                                <img className="ni__img" src={item.imageUrl} alt="" loading="lazy" />
                            }
                        </div>
                        <div className="ni__body">
                            <div className="ni__h">{item.title}</div>
                            <div className="ni__d">{formatDate(item.date)}</div>
                        </div>
                    </a>)}
            </div>

            {/* Says how many, so the control is a choice rather than a mystery.
                One-way on purpose — nobody collapses a list they just opened. */}
            {hidden > 0 &&
                <button
                    type="button"
                    className="game-news__more"
                    onClick={() => setShowAll(true)}
                >
                    Show {hidden} more
                </button>
            }
        </section>
    );
}
